import React from "react";
import { createPortal } from "react-dom";
import Button, { ButtonProps } from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { skipLinkRegistry } from "./skipLinkRegistry";

/* SKIP LINKS 
For React components, use the useSkipLinks hook with an element id, ref, or target resolver
    useSkipLinks([
        {
            label: "Skip to Search",
            targetId: "search-bar-input", 
            priority: 20
        } 
    ])
*/

type BaseSkipLink = {
  label: string;
  priority?: number; // enforces desired skip link order - default is 1000
};

type SkipLinkById = BaseSkipLink & {
  targetId: string;
  targetRef?: never;
  targetResolver?: never;
};

type SkipLinkByRef = BaseSkipLink & {
  targetRef: React.RefObject<HTMLElement>;
  targetId?: never;
  targetResolver?: never;
};

type SkipLinkByResolver = BaseSkipLink & {
  targetResolver: () => HTMLElement | null;
  targetId?: never;
  targetRef?: never;
};

export type SkipLinkInput = SkipLinkById | SkipLinkByRef | SkipLinkByResolver;

export type SkipLinkConfig = SkipLinkInput & {
  id: string;
};

export const SkipLinksProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [links, setLinks] = React.useState<SkipLinkConfig[]>([]);

  React.useEffect(() => {
    return skipLinkRegistry.subscribe(setLinks);
  }, []);

  return (
    <>
      {children}
      <SkipLinksRenderer links={links} />
    </>
  );
};

// Render skip links
const SkipLinksRenderer: React.FC<{ links: SkipLinkConfig[] }> = ({
  links,
}) => {
  const portalContainer = document.getElementById("skip-link-portal");

  if (!portalContainer) return null;

  const focusElement = (el: HTMLElement) => {
    if (!el.hasAttribute("tabindex")) {
      el.setAttribute("tabindex", "-1");
    }
    el.focus();
  };

  // Sets focus on the desired target
  const handleClickSkipLink = (
    e: React.MouseEvent<HTMLElement>,
    link: SkipLinkConfig,
  ): void => {
    let isHandled = false;
    const { targetId, targetRef } = link;

    if (link.targetResolver) {
      const el = link.targetResolver();
      if (el) {
        el.scrollIntoView();
        focusElement(el);
        isHandled = true;
      }
    }

    if (targetRef?.current) {
      targetRef.current.scrollIntoView({ block: "start" });
      focusElement(targetRef.current);
      isHandled = true;
    }

    if (!isHandled && targetId) {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ block: "start" });
        focusElement(el);
        isHandled = true;
      }
    }

    // Block default only if event has been handled. Otherwise fallback to default
    if (isHandled) {
      e.preventDefault();
    }
  };

  const sortedLinks = [...links].sort((a, b) => {
    return (a.priority ?? 1000) - (b.priority ?? 1000);
  });

  return createPortal(
    <>
      {sortedLinks.map((link) => (
        <SkipLinkButton
          href={link.targetId ? `#${link.targetId}` : undefined}
          onClick={(e) => handleClickSkipLink(e, link)}
          component="a"
          key={link.id}
          variant="outlined"
          tabIndex={1}
        >
          {link.label}
        </SkipLinkButton>
      ))}
    </>,
    portalContainer,
  );
};

export const useSkipLinks = (links: SkipLinkInput[]) => {
  const idsRef = React.useRef<string[]>([]);

  // stable ids
  if (idsRef.current.length !== links.length) {
    idsRef.current = links.map(
      () => `skip-${Math.random().toString(36).slice(2)}`,
    );
  }

  React.useEffect(() => {
    let cancelled = false;
    const currentIds = idsRef.current;

    queueMicrotask(() => {
      if (cancelled) return;

      links.forEach((link, index) => {
        skipLinkRegistry.register({
          ...link,
          id: currentIds[index],
        });
      });
    });

    return () => {
      cancelled = true;
      currentIds.forEach((id) => {
        skipLinkRegistry.unregister(id);
      });
    };
  }, [links]);
};

const SkipLinkButton = styled(Button)<ButtonProps>(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  display: "block",
  backgroundColor: "#fff",
  margin: theme.spacing(2),
  overflow: "hidden",
  whiteSpace: "nowrap",
  zIndex: -9999,
  opacity: 0,

  "&:focus": {
    zIndex: 9999,
    opacity: 1,
  },
}));
